<script setup lang="ts">
const { t: $t } = useI18n()

import { startOfDay } from 'date-fns'
import type { MachineryCategories, UpsertUVVReportInput } from '~/types'
import { MachineryUVVReportInspectionType, MachineryUVVReportMachineryCondition } from '~/prisma/enums'

type ReportItemConfigKey = keyof MachineryCategoryMachineryUVVReportItemConfig

const props = defineProps<{
  payload: UpsertUVVReportInput
  machineryCategory: MachineryCategories
  readOnly?: boolean
}>()
defineEmits<{ (e: 'save', data: UpsertUVVReportInput): void }>()

const inspectionTypeOptions = computed(() => Object.values(MachineryUVVReportInspectionType).map(value => ({
  label: $t(`machinery.uvvReport.inspectionType.${props.machineryCategory}.options.${value}`),
  value,
})))
const machineryConditionOptions = computed(() => Object.values(MachineryUVVReportMachineryCondition).map(value => ({
  label: $t(`machinery.uvvReport.machineryCondition.${props.machineryCategory}.options.${value}`),
  value,
})))
const isReadyForOperationOptions = computed(() => [
  { label: $t(`machinery.uvvReport.isReadyForOperation.${props.machineryCategory}.options.yes`), value: true },
  { label: $t(`machinery.uvvReport.isReadyForOperation.${props.machineryCategory}.options.no`), value: false },
])

const uvvReportConfig = machineryUVVReportConfigByMachineryCategories[props.machineryCategory]
const uvvReportConfigItemsEntries = Object.entries(uvvReportConfig?.items ?? {}) as [ReportItemConfigKey, NonNullable<MachineryCategoryMachineryUVVReportItemConfig[ReportItemConfigKey]>][]
const uvvReportConfigItems = uvvReportConfigItemsEntries.flatMap(([_, types]) => types)

// InspectionItems mapped by type
const inspectionItems = ref(Object.fromEntries(props.payload.inspectionItems.map(item => [item.type, { ...item }])))
const inspectionItemHeadersIsSkipped = ref<Partial<Record<ReportItemConfigKey, boolean>>>(
  uvvReportConfig
    ? Object.fromEntries(uvvReportConfigItemsEntries.map(([key]) => [key, isAllInspectionItemsInSectionSkipped(key)]))
    : {},
)

function updateIsSkippedForHeaderItems(value: boolean, header: ReportItemConfigKey) {
  if (!uvvReportConfig) {
    return
  }

  const types = uvvReportConfig.items[header]?.map(({ type }) => type) ?? []
  for (const type of types) {
    inspectionItems.value[type].isSkipped = value
  }
}

function updateAllInspectionItemsToTrue() {
  for (const { type, fields } of uvvReportConfigItems) {
    if (fields.isSecurityCheckInOrder) {
      inspectionItems.value[type].isSecurityCheckInOrder = true
    }
    if (fields.isMeasurementInOrder) {
      inspectionItems.value[type].isMeasurementInOrder = true
    }
    if (fields.isComponentCompleted) {
      inspectionItems.value[type].isComponentCompleted = true
    }
  }
}

function isAllInspectionItemsInSectionSkipped(header: ReportItemConfigKey) {
  const types = uvvReportConfig?.items[header]?.map(({ type }) => type) ?? []
  return types.every(type => inspectionItems.value[type].isSkipped)
}
</script>

<template>
  <FormKit
    v-if="uvvReportConfig"
    type="form"
    :actions="readOnly ? false : undefined"
    :value="payload"
    @submit="$emit('save', { ...$event, inspectionItems: Object.values(inspectionItems) })"
  >
    <TheDevOnlyNiceJson :value="inspectionItems" />

    <div class="flex gap-4">
      <div class="w-1/2">
        <FormKitRadioWithReset
          v-if="uvvReportConfig.reportFields.has('machineryCondition')"
          name="machineryCondition"
          :label="$t(`machinery.uvvReport.machineryCondition.${machineryCategory}.label`)"
          :options="machineryConditionOptions"
          :disabled="readOnly"
          validation="required"
        />
      </div>
      <div class="w-1/2">
        <FormKitRadioWithReset
          v-if="uvvReportConfig.reportFields.has('inspectionType')"
          name="inspectionType"
          :label="$t(`machinery.uvvReport.inspectionType.${machineryCategory}.label`)"
          :options="inspectionTypeOptions"
          :disabled="readOnly"
          validation="required"
        />
      </div>
    </div>
    <div v-if="!readOnly" class="flex justify-end mb-4">
      <n-button @click="updateAllInspectionItemsToTrue">
        {{ $t('machinery.uvvReport.inspectionItems.updateAllToTrue') }}
      </n-button>
    </div>
    <n-collapse display-directive="show">
      <n-collapse-item v-for="([headerKey, types]) of uvvReportConfigItemsEntries" :key="headerKey" :name="headerKey">
        <template #header>
          <div class="w-full flex gap-2 justify-between">
            <h3 class="text-lg font-semibold">
              {{ $t(`machinery.uvvReport.inspectionItems.${machineryCategory}.header.${headerKey}.label`) }}
            </h3>
            <label v-if="!readOnly" class="flex gap-2 justify-between" @click.stop>
              <n-switch
                v-model:value="inspectionItemHeadersIsSkipped[headerKey]"
                size="large"
                @update:value="updateIsSkippedForHeaderItems($event, headerKey)"
              />
              <span class="font-bold">{{ $t('machinery.uvvReport.inspectionItems.isSkipped.label') }}</span>
            </label>
          </div>
        </template>
        <div class="space-y-4">
          <div
            v-for="({ type, fields }) of types"
            :key="type"
            class="border border-gray-300 bg-gray-100 rounded-lg p-4 grid grid-cols-2 gap-4"
          >
            <div class="col-span-2 flex gap-2 justify-between">
              <p class="text-base font-semibold">
                {{ $t(`machinery.uvvReport.inspectionItems.${machineryCategory}.${type}.label`) }}
              </p>
              <label v-if="!readOnly" class="flex gap-2 items-center">
                <n-switch
                  v-model:value="inspectionItems[type].isSkipped"
                  :disabled="inspectionItemHeadersIsSkipped[headerKey]"
                  size="large"
                />
                <span class="font-bold">{{ $t('machinery.uvvReport.inspectionItems.isSkipped.label') }}</span>
              </label>
            </div>

            <template v-if="!inspectionItems[type].isSkipped">
              <FormKitNumberNaiveUi
                v-if="fields.tons"
                v-model="inspectionItems[type].tons"
                :classes="{ outer: 'col-span-2' }"
                :validation="fields.tons.isRequired ? 'required' : undefined"
                :label="$t(`machinery.uvvReport.inspectionItems.${machineryCategory}.tons.label`)"
                :readonly="readOnly"
              />
              <FormKitNumberNaiveUi
                v-if="fields.liftingWeight"
                v-model="inspectionItems[type].liftingWeight"
                :classes="{ outer: 'col-span-2' }"
                :validation="fields.liftingWeight.isRequired ? 'required' : undefined"
                :label="$t(`machinery.uvvReport.inspectionItems.${machineryCategory}.liftingWeight.label`)"
                :readonly="readOnly"
              />
              <div v-if="fields.isSecurityCheckInOrder" class="bg-white rounded-lg px-2">
                <FormKitRadioWithReset
                  v-model="inspectionItems[type].isSecurityCheckInOrder"
                  :label="$t(`machinery.uvvReport.inspectionItems.${machineryCategory}.isSecurityCheckInOrder.label`)"
                  :validation="fields.isSecurityCheckInOrder.isRequired ? 'required' : undefined"
                  :disabled="readOnly"
                />
              </div>
              <div v-if="fields.isMeasurementInOrder" class="bg-white rounded-lg px-2">
                <FormKitRadioWithReset
                  v-model="inspectionItems[type].isMeasurementInOrder"
                  :label="$t(`machinery.uvvReport.inspectionItems.${machineryCategory}.isMeasurementInOrder.label`)"
                  :validation="fields.isMeasurementInOrder.isRequired ? 'required' : undefined"
                  :disabled="readOnly"
                />
              </div>
              <div v-if="fields.isComponentCompleted" class="bg-white rounded-lg px-2">
                <FormKitRadioWithReset
                  v-model="inspectionItems[type].isComponentCompleted"
                  :label="$t(`machinery.uvvReport.inspectionItems.${machineryCategory}.isComponentCompleted.label`)"
                  :validation="fields.isComponentCompleted.isRequired ? 'required' : undefined"
                  :disabled="readOnly"
                />
              </div>
              <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
              <FormKit
                v-if="fields.value"
                v-model="inspectionItems[type].value"
                type="text"
                :validation="fields.value.isRequired ? 'required' : undefined"
                :label="$t(`machinery.uvvReport.inspectionItems.${machineryCategory}.value.label`)"
                :readonly="readOnly"
              />
              <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
              <FormKit
                v-if="fields.remark"
                v-model="inspectionItems[type].remark"
                type="text"
                :validation="fields.remark.isRequired ? 'required' : undefined"
                :label="$t('machinery.uvvReport.inspectionItems.remark.label')"
                :readonly="readOnly"
              />
              <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. To enable the upgrade we add this ignore: The TS-error was there before, it was just not caught. Now, its at least explicit and can be fixed in the future. TODO: Fix, improve or replace once we agree on a systematic fix -->
              <FormKit
                v-if="fields.rectifiedDefects"
                v-model="inspectionItems[type].rectifiedDefects"
                type="text"
                :validation="fields.rectifiedDefects.isRequired ? 'required' : undefined"
                :label="$t('machinery.uvvReport.inspectionItems.rectifiedDefects.label')"
                :readonly="readOnly"
              />
            </template>
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>

    <FormKitFileDropzone
      :classes="{ outer: 'mt-10' }"
      name="photos"
      :label="$t('machinery.uvvReport.photos.label')"
      :read-only
    />

    <div class="w-1/2">
      <FormKitRadioWithReset
        v-if="uvvReportConfig.reportFields.has('isReadyForOperation')"
        name="isReadyForOperation"
        :label="$t(`machinery.uvvReport.isReadyForOperation.${machineryCategory}.label`)"
        :disabled="readOnly"
        :options="isReadyForOperationOptions"
        validation="required"
      />
    </div>

    <div class="grid grid-cols-2 gap-2">
      <FormKitDate
        name="inspectionDate"
        :label="$t('machinery.uvvReport.inspectionDate.label')"
        :transform-date="startOfDay"
        validation="required"
        :read-only
      />

      <FormKitDate
        name="nextUVVRequiredDate"
        :label="$t('machinery.nextUVVRequiredDate.label')"
        :transform-date="startOfDay"
        validation="required"
        :read-only
      />

      <FormKit
        name="operatingHours"
        :label="$t('machinery.operatingHours.label')"
        step="0.1"
        :default="1"
        type="number"
        number
        validation="required|min:0"
      />

      <FormKit
        name="signatoryName"
        :label="$t('machinery.uvvReport.signatoryName.label')"
        type="text"
        validation="required"
        :readonly="readOnly"
      />
      <FormKitSignaturePopup
        name="signature"
        :label="$t('machinery.uvvReport.signature.label')"
        :title="$t('machinery.uvvReport.signature.title')"
        :button-text="$t('machinery.uvvReport.signature.buttonText')"
        :disable-by-pass="true"
        :disable-positions-overview="true"
        :default="undefined"
        :read-only
        validation="required"
      />
    </div>
  </FormKit>
  <p v-else>
    {{ $t('machinery.uvvReport.notSupportedCategory') }}
  </p>
</template>
